import * as React from "react";
import { useNavigate } from "react-router-dom";
import PlacementTestEntry from "./PlacementTestEntry";
import UserPlacementTest from "./UserPlacementTest"; // Assuming you have this component as per previous conversation
import PlacementTestResult from "./PlacementTestResult";
import {
  PlacementTest,
  StudentPlacementTestAnswer,
  StudentPlacementTestQuestion,
  StudentPlacementTestResponse,
} from "../../types/PlacementTest";
import * as placementTestService from "../../services/placementTestService";
import { getUserProfile } from "../../services/userService";
import { PlacementQuestion } from "../../types/PlacementQuestion";
import { AxiosError } from "axios";
import { ApiErrorResponse } from "../../utils/errorHandling";
import { useHandleError } from "../../hooks/useHandleError";
import { useAuth } from "../../hooks/useAuth";

const PlacementTestPage: React.FC = () => {
  const handleError = useHandleError();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [step, setStep] = React.useState(0);

  const [questions, setQuestions] = React.useState<
    StudentPlacementTestQuestion[]
  >([]);

  const [answers, setAnswers] = React.useState<StudentPlacementTestAnswer[]>(
    []
  );

  const [currentRemainingTime, setCurrentRemainingTime] = React.useState(
    20 * 60
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = React.useState(0);
  const [result, setResult] = React.useState<any>(null); // Update the type as needed
  // const [userEmail, setUserEmail] = React.useState<any>(null);

  // Refresh the user profile, then leave the placement-test page. StudentGuard
  // routes the student to the correct next screen (waiting-assignment if no
  // classroom yet, my-classroom if already active, etc).
  const exitToOnboarding = React.useCallback(async () => {
    try {
      const profile = await getUserProfile();
      if (profile?.data) setUser(profile.data);
    } catch (err) {
      // Non-fatal — still route out so the student is not trapped.
      console.warn("Failed to refresh profile after placement test:", err);
    }
    navigate("/student/waiting-assignment", { replace: true });
  }, [navigate, setUser]);

  const handleStartTest = async () => {
    try {
      // setUserData(data);
      const response = await placementTestService.createPlacementTest();
      const placementTest: StudentPlacementTestResponse = response.data;

      // Already completed or expired: the backend saved the result.
      // Skip the question UI and let StudentGuard route onward.
      if (placementTest.result || placementTest.status) {
        await exitToOnboarding();
        return;
      }

      const fetchedQuestions = placementTest?.questions;

      const lastIndex = findNextQuestionIndex(
        fetchedQuestions,
        placementTest.userAnswers
      );

      console.log("QUESTION INDEX->", lastIndex);

      if (lastIndex > 0) {
        setCurrentQuestionIndex(lastIndex);
      }

      setCurrentRemainingTime(placementTest?.timeRemaining);

      setQuestions(fetchedQuestions);

      setAnswers(placementTest.userAnswers);

      setStep(1);
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleCompleteTest = React.useCallback(
    async (answer: StudentPlacementTestAnswer) => {
      const updatedAnswers = validateAnswers(answer);
      //there could be questions without answers
      const nonEmptyAnswers = updatedAnswers.filter((a) => a.userAnswer);
      try {
        await placementTestService.finshPlacementTest(nonEmptyAnswers);
      } catch (error) {
        // Log the failure but don't strand the student on the question page.
        // The backend persists the result server-side when the window lapses,
        // so exiting is safe even if this submit failed.
        handleError(error);
      }
      await exitToOnboarding();
    },
    // validateAnswers closes over `answers`; include it so the latest
    // answer map is used when the effect fires after a re-render.
    [answers, handleError, exitToOnboarding]
  );

  const handleSaveProgress = React.useCallback(
    async (answer: StudentPlacementTestAnswer) => {
      const updatedAnswers = validateAnswers(answer);
      try {
        placementTestService.savePlacementTestProgress(updatedAnswers);
      } catch (error) {
        handleError(error);
      }
    },
    [answers, handleError]
  );

  const validateAnswers = (
    answer: StudentPlacementTestAnswer
  ): StudentPlacementTestAnswer[] => {
    const updatedAnswers = answers.map((a) =>
      a._id === answer._id ? answer : a
    );

    if (!answers.find((a) => a._id === answer._id)) {
      updatedAnswers.push(answer); // Add the new answer if it doesn't exist
    }

    setAnswers(updatedAnswers); // Update state
    return updatedAnswers; // Return the updated answers
  };

  React.useEffect(() => {}, []); // The empty array means this useEffect runs once when the component mounts

  const findNextQuestionIndex = (
    questions: StudentPlacementTestQuestion[],
    userAnswer: StudentPlacementTestAnswer[]
  ) => {
    const questionIndex = questions.findIndex(
      (q) => !userAnswer.find((ua) => ua._id == q._id)
    );

    return questionIndex;
  };

  return (
    <div>
      {step === 0 && <PlacementTestEntry onStartTest={handleStartTest} />}
      {step === 1 && (
        <UserPlacementTest
          questions={questions}
          currentQuestionIndex={currentQuestionIndex}
          currentRemainingTime={currentRemainingTime}
          onComplete={handleCompleteTest}
          onProgress={handleSaveProgress}
        />
      )}
      {step === 2 && <PlacementTestResult result={result} />}
    </div>
  );
};

export default PlacementTestPage;
